import { closeFlyout, isFlyoutOpen, openFlyout } from './header-flyout.js';

const NAV_LINK_HOVER_DELAY = 150;

export function initHeaderMegaMenu() {
  const toggle = document.querySelector('[data-catalog-toggle]');
  const panel = document.querySelector('[data-catalog-dropdown]');

  if (!toggle || !panel || panel.dataset.megaMenuInited === '1') return;

  panel.dataset.megaMenuInited = '1';

  const navLinks = panel.querySelectorAll('.nav-link');
  const leaveDuration = 220;
  let closeTimer;
  let switchTimer;
  let hoverTimer;
  const dropdownPools = new WeakMap();

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const hasDropdown = (link) => Boolean(link?.querySelector(':scope > .dropdown'));

  const defaultMinHeight = Number.parseFloat(window.getComputedStyle(panel).minHeight) || 316;
  const overflowExtraRows = 2;
  const viewportGap = 16;

  const getSidebarHeight = () => {
    const nav = panel.querySelector('.nav-links');
    return nav ? nav.scrollHeight : 0;
  };

  const collectDropdownPool = (dropdown) => {
    if (dropdownPools.has(dropdown)) {
      return dropdownPools.get(dropdown);
    }

    const list = dropdown.querySelector(':scope > ul');
    if (!list) {
      return null;
    }

    const flowGroups = [...list.querySelectorAll(':scope > li.dropdown-flow-group')];
    const flatParent = list.querySelector(':scope > li.dropdown-link:has(> .without-children)');
    let units = [];
    const groupOf = new Map();

    if (dropdown.dataset.dropdownFlow === 'stack' && flowGroups.length) {
      const pool = { list, units, groupOf, groups: flowGroups };
      dropdownPools.set(dropdown, pool);
      return pool;
    }

    if (flowGroups.length) {
      flowGroups.forEach((group, groupIndex) => {
        [...group.children].forEach((child) => {
          units.push(child);
          groupOf.set(child, groupIndex);
        });
      });
    } else if (flatParent) {
      units = [...flatParent.querySelectorAll(':scope > .without-children')];
    }

    const pool = { list, units, groupOf };
    dropdownPools.set(dropdown, pool);
    return pool;
  };

  const getAvailableColumnHeight = () => Math.max(defaultMinHeight, getSidebarHeight());

  const unitStep = (item) => {
    const style = window.getComputedStyle(item);
    const margin =
      (Number.parseFloat(style.marginTop) || 0) + (Number.parseFloat(style.marginBottom) || 0);
    return item.getBoundingClientRect().height + margin;
  };

  const countFit = (items, limit) => {
    let used = 0;
    let fit = 0;
    for (let index = 0; index < items.length; index += 1) {
      const item = items[index];
      const step = unitStep(item);
      if (index > 0 && used + step > limit) {
        break;
      }
      used += step;
      fit += 1;
    }
    return Math.max(1, fit);
  };

  const extendFitToGroup = (units, fit, groupOf, limit) => {
    if (!groupOf?.size || fit >= units.length) {
      return fit;
    }

    const group = groupOf.get(units[fit - 1]);
    if (group == null || groupOf.get(units[fit]) !== group) {
      return fit;
    }

    let used = 0;
    for (let index = 0; index < fit; index += 1) {
      used += unitStep(units[index]);
    }

    let next = fit;
    while (next < units.length && groupOf.get(units[next]) === group) {
      const add = unitStep(units[next]);
      if (used + add > limit) {
        break;
      }
      used += add;
      next += 1;
    }

    return next;
  };

  const createMoreButton = () => {
    const moreButton = document.createElement('button');
    moreButton.type = 'button';
    moreButton.className = 'dropdown-link__more';
    moreButton.dataset.dropdownMore = '';
    moreButton.setAttribute('aria-expanded', 'false');
    moreButton.textContent = 'Ещё';
    return moreButton;
  };

  const layoutStackGroupColumns = (pool, { preserveWide = false } = {}) => {
    const { list, groups } = pool;
    list.classList.remove('has-two-columns');
    groups.forEach((group) => {
      group.classList.add('dropdown-grid-column');
      group.classList.remove('is-hidden');
    });
    list.replaceChildren(...groups);
    if (groups.length >= 2) {
      list.classList.add('has-two-columns');
      if (!preserveWide) {
        panel.classList.add('is-wide');
      }
      return;
    }
    if (!preserveWide) {
      panel.classList.remove('is-wide');
    }
  };

  const layoutOverflowColumns = (dropdown, { preserveWide = false } = {}) => {
    const pool = collectDropdownPool(dropdown);
    if (pool?.groups?.length) {
      layoutStackGroupColumns(pool, { preserveWide });
      return;
    }
    if (!pool || pool.units.length === 0) {
      if (!preserveWide) {
        panel.classList.remove('is-wide');
      }
      return;
    }

    const { list, units, groupOf } = pool;
    const expanded = dropdown.dataset.moreExpanded === '1';
    const listStyles = window.getComputedStyle(list);
    const pad =
      (Number.parseFloat(listStyles.paddingTop) || 0) +
      (Number.parseFloat(listStyles.paddingBottom) || 0);
    const moreReserve = 32;
    const innerFromAvailable = (height) => Math.max(32, height - pad);
    const fitAt = (limit) =>
      extendFitToGroup(units, Math.min(units.length, countFit(units, limit)), groupOf, limit);
    const secondFitAt = (items, limit) =>
      Math.min(
        items.length,
        extendFitToGroup(items, countFit(items, limit), groupOf, limit)
      );

    list.classList.remove('has-two-columns');
    units.forEach((unit) => unit.classList.remove('is-hidden'));

    const measureColumn = document.createElement('li');
    measureColumn.className = 'dropdown-link dropdown-grid-column';
    units.forEach((unit) => measureColumn.appendChild(unit));
    list.replaceChildren(measureColumn);
    void list.offsetHeight;

    let inner = innerFromAvailable(getAvailableColumnHeight());
    const storedFit = Number.parseInt(dropdown.dataset.columnFit || '', 10);
    let fit = fitAt(inner);

    if (expanded && storedFit > 0) {
      fit = Math.min(storedFit, units.length);
    }

    if (fit >= units.length) {
      delete dropdown.dataset.columnFit;
      if (!preserveWide) {
        panel.classList.remove('is-wide');
      }
      return;
    }

    if (!expanded) {
      const previewSecond = units.slice(fit);
      const compactSecondLimit = Math.max(32, inner - moreReserve);
      if (secondFitAt(previewSecond, compactSecondLimit) < previewSecond.length) {
        inner = innerFromAvailable(
          getAvailableColumnHeight() + unitStep(units[0]) * overflowExtraRows
        );
        fit = fitAt(inner);
      }
    }

    if (fit >= units.length) {
      delete dropdown.dataset.columnFit;
      if (!preserveWide) {
        panel.classList.remove('is-wide');
      }
      return;
    }

    if (!expanded) {
      dropdown.dataset.columnFit = String(fit);
    }

    const secondUnits = units.slice(fit);
    const secondLimit = Math.max(32, inner - moreReserve);
    const secondFit = expanded
      ? secondUnits.length
      : secondFitAt(secondUnits, secondLimit);

    const first = document.createElement('li');
    const second = document.createElement('li');
    first.className = 'dropdown-link dropdown-grid-column';
    second.className = 'dropdown-link dropdown-grid-column';
    units.slice(0, fit).forEach((unit) => first.appendChild(unit));
    secondUnits.forEach((unit, index) => {
      if (!expanded && index >= secondFit) {
        unit.classList.add('is-hidden');
      }
      second.appendChild(unit);
    });
    if (!expanded && secondFit < secondUnits.length) {
      second.appendChild(createMoreButton());
    }
    list.replaceChildren(first, second);

    list.classList.add('has-two-columns');
    if (!preserveWide) {
      panel.classList.add('is-wide');
    }
  };

  const getDropdownContentHeight = (dropdown) => {
    if (!dropdown) return 0;
    const list = dropdown.querySelector(':scope > ul');
    if (!list) return dropdown.scrollHeight;

    const previousHeight = list.style.height;
    list.style.height = 'auto';
    const height = list.scrollHeight;
    if (previousHeight) {
      list.style.height = previousHeight;
    } else {
      list.style.removeProperty('height');
    }
    return height;
  };

  const syncPanelHeight = (link, { preserveWide = false } = {}) => {
    const dropdown = link?.querySelector(':scope > .dropdown');
    if (dropdown) {
      layoutOverflowColumns(dropdown, { preserveWide });
    } else if (!preserveWide) {
      panel.classList.remove('is-wide');
    }

    const needed = Math.max(
      defaultMinHeight,
      getSidebarHeight(),
      getDropdownContentHeight(dropdown)
    );
    const panelTop = panel.getBoundingClientRect().top;
    const maxHeight = Math.max(defaultMinHeight, window.innerHeight - panelTop - viewportGap);
    let height = Math.min(needed, maxHeight);

    // Пока на экране предыдущий пункт, ширину не меняем.
    // Замер нового двухколоночного меню в узкой панели завышает высоту
    // (переносы строк) — не даём панели вырасти, сжать можно.
    if (preserveWide) {
      const currentHeight = panel.getBoundingClientRect().height;
      if (currentHeight > 0) {
        height = Math.min(height, currentHeight);
      }
    }

    panel.style.minHeight = `${height}px`;
    panel.style.height = `${height}px`;

    panel.querySelectorAll('.dropdown').forEach((item) => {
      item.classList.toggle('is-scrollable', item === dropdown && needed > maxHeight);
    });
  };

  const resetPanelHeight = () => {
    panel.style.removeProperty('min-height');
    panel.style.removeProperty('height');
    panel.style.removeProperty('width');
    panel.classList.remove('is-wide');
    panel.querySelectorAll('.dropdown.is-scrollable').forEach((item) => {
      item.classList.remove('is-scrollable');
    });
  };

  const resetDropdownScroll = (link, smooth = true) => {
    const dropdown = link?.querySelector(':scope > .dropdown');
    if (!dropdown) return;

    const behavior = smooth && !prefersReducedMotion() ? 'smooth' : 'auto';
    dropdown.scrollTo({ top: 0, behavior });
  };

  const clearHoverDelay = () => {
    clearTimeout(hoverTimer);
  };

  const clearSwitchState = () => {
    clearTimeout(switchTimer);
    clearHoverDelay();
    navLinks.forEach((item) => item.classList.remove('is-leaving'));
  };

  const close = ({ instant = false } = {}) => {
    toggle.setAttribute('aria-expanded', 'false');
    toggle.classList.remove('is-open');
    clearSwitchState();
    if (!instant) {
      navLinks.forEach((item) => item.classList.remove('is-active', 'is-shown'));
    }
    clearTimeout(closeTimer);
    resetPanelHeight();
    closeFlyout(panel, { instant });
  };

  const showDropdown = (link, smoothScroll = true) => {
    clearSwitchState();
    navLinks.forEach((item) => {
      item.classList.remove('is-shown');
    });

    if (!hasDropdown(link)) {
      return;
    }

    link.classList.add('is-shown');
    const dropdown = link.querySelector(':scope > .dropdown');
    if (dropdown) {
      delete dropdown.dataset.moreExpanded;
    }
    syncPanelHeight(link);
    resetDropdownScroll(link, smoothScroll);
  };

  const setActive = (link, smoothScroll = true) => {
    if (!link) return;

    navLinks.forEach((item) => item.classList.remove('is-active'));
    link.classList.add('is-active');

    if (!hasDropdown(link)) {
      clearSwitchState();
      navLinks.forEach((item) => item.classList.remove('is-shown'));
      syncPanelHeight(link);
      return;
    }

    const shown = panel.querySelector('.nav-link.is-shown');

    if (shown === link) {
      return;
    }

    clearTimeout(switchTimer);

    if (shown && shown !== link && !prefersReducedMotion()) {
      const leavingDropdown = shown.querySelector(':scope > .dropdown');
      if (leavingDropdown) {
        delete leavingDropdown.dataset.moreExpanded;
      }
      shown.classList.remove('is-shown');
      shown.classList.add('is-leaving');
      syncPanelHeight(link, { preserveWide: true });

      switchTimer = window.setTimeout(() => {
        shown.classList.remove('is-leaving');
        showDropdown(link, smoothScroll);
      }, leaveDuration);

      return;
    }

    if (shown) {
      shown.classList.remove('is-shown', 'is-leaving');
    }

    showDropdown(link, smoothScroll);
  };

  const setActiveOnHover = (link) => {
    clearHoverDelay();

    if (link.classList.contains('is-shown')) {
      return;
    }

    const shown = panel.querySelector('.nav-link.is-shown');
    if (shown && shown !== link) {
      const leavingDropdown = shown.querySelector(':scope > .dropdown');
      if (leavingDropdown?.dataset.moreExpanded === '1') {
        delete leavingDropdown.dataset.moreExpanded;
        syncPanelHeight(link, { preserveWide: true });
      }
    }

    const delay = prefersReducedMotion() ? 0 : NAV_LINK_HOVER_DELAY;

    hoverTimer = window.setTimeout(() => {
      setActive(link);
    }, delay);
  };

  const getDefaultCatalogLink = () => {
    const byName = [...navLinks].find((link) => {
      const label = link.querySelector(':scope > a span, :scope > span span')?.textContent?.trim();
      return label === 'Букеты';
    });
    return byName || panel.querySelector('.nav-link.has-childs') || navLinks[0];
  };

  const open = ({ instant = false } = {}) => {
    clearTimeout(closeTimer);
    clearSwitchState();
    toggle.setAttribute('aria-expanded', 'true');
    toggle.classList.add('is-open');
    navLinks.forEach((item) => item.classList.remove('is-active', 'is-shown', 'is-leaving'));
    openFlyout(panel, { instant });

    const defaultLink = getDefaultCatalogLink();
    if (defaultLink) {
      setActive(defaultLink, false);
    }
  };

  const isPanelOpen = () => isFlyoutOpen(panel);
  const searchWrap = document.getElementById('header-search');
  const searchPanel = document.getElementById('header-search-panel');

  window.addEventListener('resize', () => {
    if (!isPanelOpen()) return;
    const activeLink = panel.querySelector('.nav-link.is-shown') || panel.querySelector('.nav-link.is-active');
    syncPanelHeight(activeLink);
  });

  toggle.addEventListener('click', (event) => {
    event.stopPropagation();
    if (isPanelOpen()) {
      close();
      return;
    }

    const fromSearch = isFlyoutOpen(searchPanel);
    if (fromSearch) {
      document.dispatchEvent(new CustomEvent('pc-header-search-close', { detail: { instant: true } }));
    }
    open({ instant: fromSearch });
  });

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Node)) {
      return;
    }
    if (
      panel.contains(target) ||
      toggle.contains(target) ||
      searchWrap?.contains(target) ||
      searchPanel?.contains(target)
    ) {
      return;
    }
    close();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isPanelOpen()) {
      close();
      toggle.focus();
    }
  });

  document.addEventListener('pc-header-catalog-close', (event) => {
    close({ instant: Boolean(event.detail?.instant) });
  });

  navLinks.forEach((link) => {
    link.addEventListener('mouseenter', () => setActiveOnHover(link));
    link.addEventListener('mouseleave', (event) => {
      const related = event.relatedTarget;
      if (related instanceof Node && panel.contains(related)) {
        return;
      }
      clearHoverDelay();
    });
    link.addEventListener('focusin', () => {
      clearHoverDelay();
      setActive(link);
    });
  });

  panel.addEventListener('click', (event) => {
    const moreButton = event.target.closest('[data-dropdown-more]');
    if (!moreButton) return;

    event.preventDefault();
    event.stopPropagation();

    const dropdown = moreButton.closest('.dropdown');
    if (dropdown) {
      dropdown.dataset.moreExpanded = '1';
    }

    const activeLink = panel.querySelector('.nav-link.is-shown') || panel.querySelector('.nav-link.is-active');
    syncPanelHeight(activeLink);
  });

}
